import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@monksflow/monks-ui';
import { ChevronRightIcon } from 'lucide-react';
import type { StreamMessageEvents } from '@/entities/ai';
import { AgentTask } from '../AgentTask';
import { FINAL_STATUS_EVENT } from '../AgentTask.constants';
import { getSseEventDisplayName, isSupportedSseTaskEvent } from '../AgentTask.utils';
import { AgentTaskIcon } from '../AgentTaskIcon';
import { TASK_ICON_CLASSES } from '../TaskItem/TaskItem.styles';

const RUNNING: ReadonlySet<string> = new Set(['data-agentStart', 'data-toolStart']);

/**
 * A reply at work, as one line: the step running now, replaced in place as the next one starts,
 * and how many it has taken. It opens onto the full tree of steps. Events arrive in the order they
 * started, so the last one still running is the innermost step under way; with none running, the
 * reply is writing its answer.
 */
export const AgentTaskStatus = memo(({ events }: { events: StreamMessageEvents[] }) => {
  const { t } = useTranslation('chats');
  const steps = useMemo(() => events.filter(isSupportedSseTaskEvent), [events]);
  const current = steps.findLast((step) => RUNNING.has(step.type)) ?? FINAL_STATUS_EVENT;

  return (
    <Collapsible data-testid="agent-task-status" className="mb-xs">
      <CollapsibleTrigger className="group flex max-w-full items-center gap-xs text-sm leading-5 text-muted-foreground transition-colors duration-fast ease-out hover:text-foreground">
        <span className={TASK_ICON_CLASSES}>
          <AgentTaskIcon data={current} />
        </span>
        <span className="min-w-0 truncate text-foreground">{getSseEventDisplayName(current)}</span>
        <span className="shrink-0">
          · {t('chats.agentTaskStatus.steps', { count: steps.length })}
        </span>
        <ChevronRightIcon
          aria-hidden
          className="size-4 shrink-0 transition-transform duration-fast ease-out group-data-[state=open]:rotate-90"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pt-2xs">
          <AgentTask events={events} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

AgentTaskStatus.displayName = 'AgentTaskStatus';
